import { Box, Stack, Typography } from '@mui/material';
import { Link } from '@liminal/web-ui/primitives/Link';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { webColors } from '@liminal/brand/semantic';
import { formatOrderDate } from '@liminal/utils/date';
import { ConversationFlag_Source } from '@/api/generated';
import type { ConversationFlag } from '@/api/types';
import { conversationFlagSourceLabel, conversationFlagState } from '@/components/ui/statusHelpers';

interface ConversationFlagNotesProps {
  /** Newest first, as the server lists them. */
  flags: readonly ConversationFlag[];
  /**
   * Quote the flagged message on each flag that has one. Under the message
   * itself that would repeat it; at the top of the thread it says which
   * message a flag is about.
   */
  quoteMessage?: boolean;
}

/** The team's flags on a reply or a conversation, read in place: the feedback, who raised it, where, and its review state. */
export function ConversationFlagNotes({ flags, quoteMessage = false }: ConversationFlagNotesProps) {
  return (
    <Stack spacing={1}>
      {flags.map((flag) => {
        const state = conversationFlagState(flag.state);
        return (
          <Box
            key={flag.id}
            data-testid={`conversation-flag-${flag.id}`}
            sx={{ borderLeft: `2px solid ${webColors.border.dark}`, pl: 1.5, py: 0.5 }}
          >
            {quoteMessage && flag.messageId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                On a message not in this thread: “{flag.messageContent || '—'}”
              </Typography>
            )}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {flag.feedback}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
              <StatusPill label={state.label} tone={state.tone} size="sm" />
              <Typography variant="caption" color="text.secondary">
                {flag.reportedBy} · {conversationFlagSourceLabel(flag.source)} · {formatOrderDate(flag.createTime)}
              </Typography>
              {flag.sourceUrl && (
                <Typography variant="caption">
                  <Link href={flag.sourceUrl} external>
                    {flag.source === ConversationFlag_Source.SOURCE_SLACK ? 'Slack thread' : 'Source'}
                  </Link>
                </Typography>
              )}
            </Stack>
            {flag.reviewNotes && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Review: {flag.reviewNotes}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
